import { Stack, TextField, TextFieldProps, Tooltip, Typography } from '@mui/material';
import { Controller, ControllerRenderProps, FieldError, FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Iconify from '../iconify';

type Props = TextFieldProps & {
  name: string;
  label?: string;
  title?: string;
  infoText?: string;
  noHelperText?: boolean;
};

export default function RHFTextField({
  name,
  helperText,
  type,
  title,
  label,
  infoText,
  placeholder,
  noHelperText = false,
  ...other
}: Props) {
  const { control } = useFormContext();
  const { t } = useTranslation();

  const placeholderOrTitle = placeholder || title;

  function renderField(field: ControllerRenderProps<FieldValues, string>, error?: FieldError) {
    const textField = (
      <TextField
        {...field}
        fullWidth
        type={type}
        placeholder={placeholderOrTitle ? t(placeholderOrTitle, { nsSeparator: false }) : undefined}
        label={label ? t(label) : undefined}
        value={type === 'number' && field.value === 0 ? '' : field.value}
        onChange={(event) => {
          if (type === 'number') {
            field.onChange(Number(event.target.value));
          } else {
            field.onChange(event.target.value);
          }
        }}
        error={!!error}
        helperText={!noHelperText && error ? error?.message : helperText}
        {...other}
      />
    );

    if (!title) return <>{textField}</>;

    return (
      <Stack spacing={1.5}>
        <Typography variant="subtitle2" sx={{ display: 'flex', gap: 1 }}>
          <span>{t(title)}</span>
          {infoText && (
            <Tooltip title={infoText} placement="right">
              <Iconify icon="mdi:information-slab-circle-outline" />
            </Tooltip>
          )}
        </Typography>
        {textField}
      </Stack>
    );
  }

  return <Controller name={name} control={control} render={({ field, fieldState: { error } }) => renderField(field, error)} />;
}
